import { Injectable } from "@angular/core";
import { EncounterStateModel } from "./encounter-model";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { EncounterFilter, EncounterService } from "../../services/encounter.service";
import { Diary, Fax, Note, NoteAddendum, ReceivedEmail, SentEmail, Users, UserService,BlockUI } from "@harlowe/core";
import { instanceToInstance, plainToClass, plainToInstance } from "class-transformer";
import { Encounters } from "./encounter-actions";
import { TealHealthEncounterDetails } from "../../models/encounter";
import { forkJoin, lastValueFrom, map, of, switchMap, tap } from "rxjs";
import { UserInterface } from "../ui/ui-actions";
import { append, iif, patch, removeItem, updateItem } from "@ngxs/store/operators";


@State<EncounterStateModel>
(
    {
        name: "encounters",
        defaults: 
        {
            encounterSearchResults: [],
            activeEncounterFilter: undefined,
            activeEncounterId: 0,
            activeEncounter: undefined
        }
    }
)

@Injectable()
export class EncounterState
{
    constructor
    (
        private _encounterService: EncounterService,
        private _userService: UserService
    )
    {}

    @Selector()
    static activeEncounterFilter(state: EncounterStateModel)
    {
       return instanceToInstance(state.activeEncounterFilter); 
    }

    @Selector()
    static encounterSearchResults(state: EncounterStateModel)
    {
         return state.encounterSearchResults;
    }
    
    @Selector()
    static activeEncounter(state: EncounterStateModel)
    {
         return state.activeEncounter;
    }


    @Selector()
    static activeEncounterId(state: EncounterStateModel)
    {
         return state.activeEncounterId;
    }


    @Action([Users.UserLoggedIn])
    async userLoggedIn(ctx: StateContext<EncounterStateModel>, action: Users.UserLoggedIn)
    {      
    }


    @Action([Encounters.EncounterFilterSet])
    async filterSet(ctx: StateContext<EncounterStateModel>, action:Encounters.EncounterFilterSet)
    {
        const state = ctx.getState();

        //Search based on the current filter
        var filter = action.payload.filter;
        var encounters = await this._encounterService.searchForEncounters(filter);

        ctx.setState
        (
            {
                ...state,
                encounterSearchResults: encounters,
                activeEncounterFilter: filter
            }
        )

        await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));
    }

    // TODO: Build logic to get store and get encounterDetails
    @Action([Encounters.EncounterSelected],{cancelUncompleted: true})
    async encounterSelected(ctx: StateContext<EncounterStateModel>,action: Encounters.EncounterSelected)
    {
        ctx.dispatch(new BlockUI.BlockUI());
        //Get the details for the encounter
        var state = ctx.getState();

        return this._encounterService.getEncounter(action.payload.encounterId)
        .pipe
        (
            switchMap
            (
                (e) =>
                {
                    var encounter = e as TealHealthEncounterDetails;
                    return forkJoin
                    (
                        [
                            this._encounterService.getEncounterNotes(encounter!.id),
                            this._encounterService.getEncounterDiaries(encounter!.id),
                            this._encounterService.getEncounterSentEmails(encounter!.id),
                            this._encounterService.getEncounterReceivedEmails(encounter!.id),
                            this._encounterService.getEncounterFaxes(encounter!.id)
                        ]
                    )
                    .pipe
                    (
                        tap
                        (
                            val => 
                            {
                                encounter!.notes = val[0].map(n => plainToClass(Note, n));
                                encounter!.diaries = val[1].map(v => plainToClass(Diary, v));
                                encounter!.sentEmails = val[2].map(e => plainToClass(SentEmail, e));
                                encounter!.receivedEmails = val[3].map(e => plainToClass(ReceivedEmail, e));
                                encounter!.outboundFaxes = val[4].map(f => plainToClass(Fax, f));

                                ctx.setState
                                (
                                    {
                                        ...state,
                                        activeEncounter: encounter,
                                        activeEncounterId: action.payload.encounterId
                                    }
                                )
                                ctx.dispatch(new BlockUI.UnBlockUI());
                            }
                        )
                    )
                }
            )

        )
    }

    @Action([Encounters.PersistState],{cancelUncompleted: true})
    async persistState(ctx:StateContext<EncounterStateModel>)
    {
        var state = ctx.getState();

        await this._userService.setSetting("encounterState",state);
    }


    @Action ([Encounters.NoteAdded, Encounters.NoteUpdated])
    async encounterNoteAdded
    (
        ctx:StateContext<EncounterStateModel>,
        action:Encounters.NoteAdded | Encounters.NoteUpdated
    )
    {
        var existingState = ctx.getState();
        if(existingState.activeEncounter?.id == action.payload.encounterId)
        {
            ctx.setState
            (
                patch<EncounterStateModel>
                ({
                    activeEncounter: patch
                    (
                        {
                            notes: iif( action instanceof Encounters.NoteAdded ,append([plainToInstance(Note,action.payload.note)]), updateItem(n => n.id == action.payload.note.id, plainToInstance(Note,action.payload.note)))
                        }
                    )
                })
            )

            await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));            
        }

    }

    @Action (Encounters.NoteDeleted)
    async encounterNoteDeleted
    (
        ctx:StateContext<EncounterStateModel>,
        action:Encounters.NoteDeleted
    )
    {
        var existingState = ctx.getState();
        if(existingState.activeEncounter?.id == action.payload.encounterId)
        {
            ctx.setState
            (
                patch<EncounterStateModel>
                ({
                    activeEncounter: patch
                    (
                        {
                            notes: removeItem<Note>(note=>note.id === action.payload.noteId)
                        }
                    )
                })
            )

            await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));
        }
    }

    @Action([Encounters.NoteAddendumAdded])
    async noteAddendumAdded
    (
        ctx: StateContext<EncounterStateModel>,
        action: Encounters.NoteAddendumAdded
    )
    {
        var existingState = ctx.getState();
        if(existingState.activeEncounter?.id == action.payload.encounterId)
        {
            ctx.setState
            (
                patch<EncounterStateModel>
                ({
                    activeEncounter: patch
                    (
                        {
                            notes: updateItem(
                                n => n.id == action.payload.noteId,
                                patch({ addenda: append([plainToInstance(NoteAddendum, action.payload.addendum)]) })
                            )
                        }
                    )
                })
            )

            await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));
        }
    }

    @Action([Encounters.DiaryAdded, Encounters.DiaryUpdated])
    async encounterDiaryAdded
    (
        ctx:StateContext<EncounterStateModel>,
        action:Encounters.DiaryAdded | Encounters.DiaryUpdated
    )
    {
        var existingState = ctx.getState();
        if(existingState.activeEncounter?.id == action.payload.encounterId)
        {
            ctx.setState
            (
                patch<EncounterStateModel>
                ({
                    activeEncounter: patch
                    (
                        {
                            diaries: iif( action instanceof Encounters.DiaryAdded ,append([plainToInstance(Diary,action.payload.diary)]), updateItem(n => n.id == action.payload.diary.id, plainToInstance(Diary,action.payload.diary)))
                        }
                    )
                })
            )

            await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));
        }
    }

    @Action([Encounters.EncounterServiceEncounterEmailSent],{cancelUncompleted: true})
    async encounterEmailSent(ctx:StateContext<EncounterStateModel>,action:Encounters.EncounterServiceEncounterEmailSent)
    {
        var existingState = ctx.getState();
        if(!existingState.activeEncounter?.sentEmails.map(e => e.id).includes(action.payload.email.id))
        {
            ctx.setState
            (
                patch<EncounterStateModel>
                ({
                    activeEncounter: patch
                    (
                        {
                            sentEmails: append([action.payload.email])
                        }
                    )
                })
            )

            await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));
        }

    }

    @Action([Encounters.EncounterServiceAttachmentsAdded])
    async encounterAttachmentsAdded(ctx:StateContext<EncounterStateModel>,action:Encounters.EncounterServiceAttachmentsAdded)
    {
        var existingState = ctx.getState();
        var existingIds = existingState.activeEncounter?.attachments.map(a => a.id);
        var attachmentsToAdd = action.payload.files.filter(a => !existingIds?.includes(a.id) )

        if(attachmentsToAdd && attachmentsToAdd.length > 0)
        {
            ctx.setState
            (
                patch<EncounterStateModel>
                ({
                    activeEncounter: patch
                    (
                        {
                            attachments: append(action.payload.files)
                        }
                    )
                })
            )
            await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));

        }

    }

    @Action([Encounters.EncounterServiceAttachmentDeleted])
    async encounterAttachmentDeleted(ctx:StateContext<EncounterStateModel>,action:Encounters.EncounterServiceAttachmentDeleted)
    {
        var existingState = ctx.getState();
        if(existingState.activeEncounter?.attachments.map(a => a.id).includes(action.payload.file.id))
        {
            var index = existingState.activeEncounter.attachments.findIndex(a => a.id == action.payload.file.id)

            ctx.setState
            (
                patch<EncounterStateModel>
                ({
                    activeEncounter: patch
                    (
                        {
                            attachments: removeItem(index)
                        }
                    )
                })
            )
            await lastValueFrom(ctx.dispatch(new Encounters.PersistState()));

        }    
        
        var exitingState = ctx.getState();

    }

}